// The composer-level control bar: the global editing chrome for the WHOLE post — undo/redo,
// the version journey, add element, and the Layers/design dock. It governs the entire composition
// (copy, layout, imagery), so it lives at the top of the composer in T3 product chrome, NOT
// floating on the image (the photo is just one layer). Per-layer tools stay on the canvas
// (FloatingToolbar). Renders nothing when the active post has no composition.

import { Image as ImageIcon, Layers, Plus, Redo2, Square, Type, Undo2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { DecorLayerMark } from '../types'
import { DECOR_MARKS, decorUri } from '../decor'
import { brandAccent } from './composition-ops'
import { useComposerEditor } from './CompositionEditorProvider'
import { ReplacePhotoPopover } from './ReplacePhotoPopover'
import { VersionStepper } from './VersionStepper'

const ICON_BTN =
  'text-paper-dim hover:bg-bg-3 hover:text-paper flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-30'
const ICON_BTN_ACTIVE = 'bg-bg-3 text-paper'
const MENU =
  'bg-bg-2 border-line text-paper overflow-hidden rounded-xl border shadow-[var(--shadow-card)]'
const MENU_ITEM =
  'text-paper-dim hover:bg-bg-3 hover:text-paper flex items-center gap-2 px-3 py-1.5 text-left text-sm'

export function ComposerEditorBar() {
  const ctx = useComposerEditor()
  const [addOpen, setAddOpen] = useState(false)
  // Cmd/Ctrl+Z / Shift+Cmd+Z as GLOBAL composer shortcuts. The canvas deselect click parks
  // focus on <body>, so a bar-local handler would never hear the keys — bind on window,
  // and stay out of the way of anything actually editing text (inputs, contentEditable:
  // the browser's own text undo must win there).
  const editorRef = useRef(ctx?.editor ?? null)
  editorRef.current = ctx?.editor ?? null
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || (e.key !== 'z' && e.key !== 'Z')) return
      const el = document.activeElement as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return
      const ed = editorRef.current
      if (!ed) return
      e.preventDefault()
      if (e.shiftKey) {
        if (ed.canRedo) ed.redo()
      } else if (ed.canUndo) {
        ed.undo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  if (!ctx) return null
  const { editor, dock, saving, saveFailed } = ctx

  const addText = () => {
    editor.addText()
    dock.openDock()
    setAddOpen(false)
  }
  const addShape = () => {
    editor.addShape()
    dock.openDock()
    setAddOpen(false)
  }
  const addImage = (attachmentId: string) => {
    editor.addImage(attachmentId)
    dock.openDock()
    setAddOpen(false)
  }
  const addDecor = (mark: DecorLayerMark) => {
    editor.addDecor(mark)
    dock.openDock()
    setAddOpen(false)
  }
  const markPreviewColor = brandAccent(editor.comp)

  // Self-contained bar (single root so it lays out correctly wherever it's mounted):
  //   LEFT  — Layers (toggles the left dock pane) then Add element.
  //   RIGHT — undo/redo + the version journey (the global history controls), pushed to the
  //           far edge with ml-auto.
  return (
    <div className="flex flex-1 items-center gap-1">
      <div className="flex items-center gap-1">
        <IconBtn
          onClick={() => dock.setOpen(!dock.open)}
          title="Layers & design"
          active={dock.open}
        >
          <Layers className="h-4 w-4" />
        </IconBtn>
        <div className="relative">
          <IconBtn onClick={() => setAddOpen(v => !v)} title="Add element" active={addOpen}>
            <Plus className="h-4 w-4" />
          </IconBtn>
          {addOpen && (
            <div className={`absolute top-full left-0 z-40 mt-1 flex w-36 flex-col py-1 ${MENU}`}>
              <MenuItem onClick={addText} icon={<Type className="h-3.5 w-3.5" />}>
                Text
              </MenuItem>
              <MenuItem onClick={addShape} icon={<Square className="h-3.5 w-3.5" />}>
                Shape
              </MenuItem>
              <ReplacePhotoPopover title="Add photo" onPick={addImage}>
                <button type="button" className={MENU_ITEM}>
                  <ImageIcon className="h-3.5 w-3.5" />
                  Photo
                </button>
              </ReplacePhotoPopover>
              <div className="px-3 pt-2 pb-1 text-[10px] font-medium tracking-wide text-paper-faint uppercase">
                Accent mark
              </div>
              <div className="flex gap-1 px-2 pb-1.5">
                {DECOR_MARKS.map(mark => (
                  <button
                    key={mark}
                    type="button"
                    title={`Add ${mark}`}
                    onClick={() => addDecor(mark)}
                    className="flex h-8 flex-1 items-center justify-center rounded-md p-1 transition-colors hover:bg-bg-3"
                  >
                    <img
                      src={decorUri(mark, markPreviewColor)}
                      alt={mark}
                      className="max-h-full max-w-full"
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1">
        {saveFailed ? (
          <span
            className="px-1.5 text-[10px] font-medium text-red-400"
            title="Your changes are safe, retrying"
          >
            Couldn’t save · retrying…
          </span>
        ) : (
          saving && <span className="px-1.5 text-[10px] text-paper-faint">Saving…</span>
        )}
        <IconBtn onClick={editor.undo} disabled={!editor.canUndo} title="Undo">
          <Undo2 className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={editor.redo} disabled={!editor.canRedo} title="Redo">
          <Redo2 className="h-4 w-4" />
        </IconBtn>
        <Divider />
        <VersionStepper />
      </div>
    </div>
  )
}

function IconBtn({
  onClick,
  disabled,
  title,
  active,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  title?: string
  active?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${ICON_BTN} ${active ? ICON_BTN_ACTIVE : ''}`}
    >
      {children}
    </button>
  )
}

const Divider = () => <span className="mx-0.5 h-4 w-px bg-line" />

function MenuItem({
  onClick,
  icon,
  children,
}: {
  onClick: () => void
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button type="button" onClick={onClick} className={MENU_ITEM}>
      {icon}
      {children}
    </button>
  )
}
